import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Collapse,
  Toolbar,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { formatCurrency } from '../../utils/formatNumber'
import { extractErrorMessage } from '../../utils/errorHandler'
import { formatDateJST } from '../../utils/dateUtils'
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel'

interface Position {
  id: number
  employee_id: string
  employee_name: string
  department_id: string
  department_name: string
  department_type: 'tech' | 'sales' | 'admin'
  assignment_date: string
  job_grade: '部長' | '課長' | '班長' | '役職なし'
  job_title: string
  rank: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I'
  monthly_cost: number
  annual_cost: number
  effective_from: string
  effective_to: string | null
  created_at: string
  updated_at: string
}

interface Department {
  department_id: string
  department_name: string
  department_type: 'tech' | 'sales' | 'admin'
}

interface RankCost {
  rank: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I'
  monthly_cost: number
  annual_cost: number
  description: string
}

interface PositionFormData {
  department_id: string
  assignment_date: string
  job_grade: '部長' | '課長' | '班長' | '役職なし'
  job_title: string
  rank: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I'
  effective_from: string
}

interface JobTitle {
  job_title_name: string
  job_title_name_en: string | null
  job_grade: '部長' | '課長' | '班長' | '役職なし'
  responsibility: string | null
  display_order: number
  is_active: boolean
  department_types: ('tech' | 'sales' | 'admin')[]
}

const PositionManagementPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<Position | null>(null)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [selectedEmployeeHistory, setSelectedEmployeeHistory] = useState<Position[] | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // 検索機能
  const [searchQuery, setSearchQuery] = useState('')

  // CSV一括登録
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvUploading, setCsvUploading] = useState(false)
  const [importErrors, setImportErrors] = useState<{ row: number; employee_id: string; message: string }[]>([])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PositionFormData>({
    defaultValues: {
      department_id: '',
      assignment_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
      job_grade: '役職なし',
      job_title: '',
      rank: 'G',
      effective_from: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
    },
  })

  const jobGrade = watch('job_grade')
  const selectedDepartmentId = watch('department_id')

  // Fetch all current positions
  const { data: positionsData, isLoading, refetch } = useQuery({
    queryKey: ['positions'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Position[] }>('/positions')
      return response.data.data
    },
  })

  // Fetch departments
  const { data: departmentsData } = useQuery({
    queryKey: ['departments-active'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Department[] }>('/departments')
      return response.data.data.filter((d: Department) => d.department_id !== 'ROOT')
    },
  })

  // Fetch rank costs
  const { data: rankCostsData } = useQuery({
    queryKey: ['rank-costs-active'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: RankCost[] }>('/rank-cost')
      return response.data.data
    },
  })

  // Fetch job titles (役職マスタ)
  const { data: jobTitlesData } = useQuery({
    queryKey: ['job-titles-active'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: JobTitle[] }>('/job-titles')
      return response.data.data
    },
  })

  // 選択中の部署の部署タイプと職級で、選べる役職を絞り込む
  const selectedDepartmentType = departmentsData?.find(
    (d) => d.department_id === selectedDepartmentId
  )?.department_type

  const availableJobTitles = (jobTitlesData || []).filter(
    (jt) =>
      jt.job_grade === jobGrade &&
      (!selectedDepartmentType || jt.department_types.includes(selectedDepartmentType))
  )

  // 職級や部署を変更すると選択済みの役職名が選択肢から外れるため、その場合はクリアする
  const currentJobTitle = watch('job_title')
  useEffect(() => {
    if (!dialogOpen || !jobTitlesData || !currentJobTitle) return
    if (!availableJobTitles.some((jt) => jt.job_title_name === currentJobTitle)) {
      setValue('job_title', '')
    }
  }, [dialogOpen, jobTitlesData, currentJobTitle, jobGrade, selectedDepartmentType])

  // Add new position mutation
  const addPositionMutation = useMutation({
    mutationFn: async (data: { employeeId: string; formData: PositionFormData }) => {
      return await api.post(`/positions/${data.employeeId}`, data.formData)
    },
    onSuccess: () => {
      toast.success('所属・職位情報を更新しました')
      queryClient.invalidateQueries({ queryKey: ['positions'] })
      setDialogOpen(false)
      setSelectedEmployee(null)
      reset()
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '所属・職位情報の更新に失敗しました'))
    },
  })

  const handleChangePosition = (position: Position) => {
    setSelectedEmployee(position)
    reset({
      department_id: position.department_id,
      assignment_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
      job_grade: position.job_grade,
      job_title: position.job_title,
      rank: position.rank,
      effective_from: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
    })
    setDialogOpen(true)
  }

  const handleViewHistory = async (employeeId: string) => {
    try {
      const response = await api.get<{
        success: boolean
        data: { employee_id: string; employee_name: string; history: Position[] }
      }>(`/positions/${employeeId}`)
      setSelectedEmployeeHistory(response.data.data.history)
      setHistoryDialogOpen(true)
    } catch (error) {
      toast.error(extractErrorMessage(error, '履歴の取得に失敗しました'))
    }
  }

  const onSubmit = async (data: PositionFormData) => {
    if (!selectedEmployee) return
    await addPositionMutation.mutateAsync({
      employeeId: selectedEmployee.employee_id,
      formData: data,
    })
  }

  const toggleRowExpansion = (employeeId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId)
      } else {
        newSet.add(employeeId)
      }
      return newSet
    })
  }

  const positions = positionsData || []
  const departments = departmentsData || []
  const rankCosts = rankCostsData || []

  // 検索フィルタリング
  const filteredPositions = useMemo(() => {
    if (!searchQuery.trim()) return positions
    const query = searchQuery.toLowerCase()
    return positions.filter(
      (pos) =>
        pos.employee_id?.toLowerCase().includes(query) ||
        pos.employee_name?.toLowerCase().includes(query) ||
        pos.department_name?.toLowerCase().includes(query) ||
        pos.job_title?.toLowerCase().includes(query)
    )
  }, [positions, searchQuery])

  // データエクスポート（CSV）
  const handleExportData = () => {
    const headers = ['employee_id', 'department_id', 'assignment_date', 'job_grade', 'job_title', 'rank', 'effective_from']

    // 現在のデータをエクスポート
    const dataRows = positions.map((pos) => {
      const assignmentDate = pos.assignment_date ? pos.assignment_date.split('T')[0] : ''
      const effectiveFrom = pos.effective_from ? pos.effective_from.split('T')[0] : ''
      return [
        pos.employee_id,
        pos.department_id || '',
        assignmentDate,
        pos.job_grade || '',
        pos.job_title || '',
        pos.rank || '',
        effectiveFrom,
      ]
    })

    const csvContent = [headers.join(','), ...dataRows.map((row) => row.join(','))].join('\n')

    const bom = '\uFEFF'
    const blob = new Blob([bom + csvContent], {
      type: 'text/csv;charset=utf-8;',
    })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `positions_export_${(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast.success(`${dataRows.length}件の所属・職位データをエクスポートしました`)
  }

  // CSVファイル選択
  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('CSVファイルを選択してください')
        return
      }
      setCsvFile(file)
    }
  }

  // CSVアップロード
  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('CSVファイルを選択してください')
      return
    }

    setCsvUploading(true)
    setImportErrors([]) // エラーをリセット

    try {
      const formData = new FormData()
      formData.append('csv_file', csvFile)

      const response = await api.post('/positions/import-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.success) {
        const { success_count, error_count, errors } = response.data.data

        if (error_count > 0) {
          // エラーを状態に保存してUIに表示
          const formattedErrors = (errors || []).map((err: any, index: number) => ({
            row: err.row || index + 2, // ヘッダー行 + 1
            employee_id: err.employee_id || '-',
            message: err.message || err.error || 'エラー',
          }))
          setImportErrors(formattedErrors)
          toast.warning(`${success_count}件成功、${error_count}件エラー。下記のエラー詳細を確認してください。`, {
            autoClose: 5000,
          })
        } else {
          toast.success(`${success_count}件の職位情報を登録しました`)
          setBulkDialogOpen(false)
          setCsvFile(null)
        }

        queryClient.invalidateQueries({ queryKey: ['positions'] })
      }
    } catch (err: any) {
      console.error('CSVアップロードエラー:', err)
      toast.error(extractErrorMessage(err, 'CSVアップロードに失敗しました'))
    } finally {
      setCsvUploading(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            所属・職位管理
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          従業員の部署、職級、役職、ランクを管理します
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ※ 職位を変更すると、履歴が自動的に記録されます
        </Typography>
      </Box>

      {/* ツールバー */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Toolbar sx={{ px: { xs: 0 }, justifyContent: 'space-between' }}>
          <TextField
            size="small"
            placeholder="社員ID、氏名、部署、役職で検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportData}>
              データエクスポート
            </Button>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setBulkDialogOpen(true)}>
              一括登録
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetch()}>
              更新
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                toast.info('社員を先にユーザー管理画面から追加してください')
              }}
            >
              新規
            </Button>
          </Box>
        </Toolbar>
      </Paper>

      {isLoading ? (
        <Typography>読み込み中...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>社員ID</TableCell>
                <TableCell>氏名</TableCell>
                <TableCell>部署</TableCell>
                <TableCell>職級</TableCell>
                <TableCell>役職</TableCell>
                <TableCell>ランク</TableCell>
                <TableCell align="right">年額コスト</TableCell>
                <TableCell align="center">発効日</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPositions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {searchQuery ? '検索条件に一致するデータがありません' : '所属・職位データがありません'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPositions.map((position) => (
                  <React.Fragment key={position.employee_id}>
                    <TableRow hover>
                      <TableCell>{position.employee_id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {position.employee_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {position.department_name || '未設定'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {position.job_grade ? (
                          <Chip
                            label={position.job_grade}
                            color={
                              position.job_grade === '部長'
                                ? 'error'
                                : position.job_grade === '課長'
                                ? 'warning'
                                : position.job_grade === '班長'
                                ? 'info'
                                : 'default'
                            }
                            size="small"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            未設定
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{position.job_title || '未設定'}</Typography>
                      </TableCell>
                      <TableCell>
                        {position.rank ? (
                          <Chip
                            label={position.rank}
                            variant="outlined"
                            color="primary"
                            size="small"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            未設定
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color={position.annual_cost ? "primary.main" : "text.secondary"}>
                          {position.annual_cost ? formatCurrency(position.annual_cost) : '未設定'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">
                          {position.effective_from ? formatDateJST(position.effective_from) : '未設定'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="職位変更">
                          <IconButton size="small" onClick={() => handleChangePosition(position)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="履歴表示">
                          <IconButton
                            size="small"
                            onClick={() => handleViewHistory(position.employee_id)}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Change Position Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>所属・職位変更</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {selectedEmployee && (
                <Alert severity={selectedEmployee.department_name ? "warning" : "info"}>
                  <Typography variant="body2">
                    <strong>{selectedEmployee.employee_name}</strong>
                    さんの所属・職位を{selectedEmployee.department_name ? '変更' : '設定'}します。
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    現在: {selectedEmployee.department_name || '未設定'} /{' '}
                    {selectedEmployee.job_title || '未設定'} / {selectedEmployee.rank || '未設定'}
                  </Typography>
                </Alert>
              )}

              <Controller
                name="department_id"
                control={control}
                rules={{ required: '部署は必須です' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.department_id}>
                    <RequiredFieldLabel label="部署" />
                    <Select {...field}>
                      {departments.map((dept) => (
                        <MenuItem key={dept.department_id} value={dept.department_id}>
                          {dept.department_name} ({dept.department_type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="assignment_date"
                control={control}
                rules={{ required: '配属日は必須です' }}
                render={({ field }) => (
                  <Box>
                    <RequiredFieldLabel label="配属日" />
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      error={!!errors.assignment_date}
                      helperText={errors.assignment_date?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                )}
              />

              <Controller
                name="job_grade"
                control={control}
                rules={{ required: '職級は必須です' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.job_grade}>
                    <RequiredFieldLabel label="職級" />
                    <Select {...field}>
                      <MenuItem value="部長">部長</MenuItem>
                      <MenuItem value="課長">課長</MenuItem>
                      <MenuItem value="班長">班長</MenuItem>
                      <MenuItem value="役職なし">役職なし</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="job_title"
                control={control}
                rules={{ required: '役職名は必須です' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.job_title}>
                    <RequiredFieldLabel label="役職名" />
                    <Select {...field}>
                      {availableJobTitles.map((jt) => (
                        <MenuItem key={jt.job_title_name} value={jt.job_title_name}>
                          {jt.job_title_name}
                          {jt.job_title_name_en ? ` (${jt.job_title_name_en})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                    {availableJobTitles.length === 0 && (
                      <FormHelperText>
                        この部署タイプと職級の組み合わせで使える役職が役職マスタに登録されていません
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="rank"
                control={control}
                rules={{ required: 'ランクは必須です' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.rank}>
                    <RequiredFieldLabel label="ランク" />
                    <Select {...field}>
                      {rankCosts.map((rankCost) => (
                        <MenuItem key={rankCost.rank} value={rankCost.rank}>
                          {rankCost.rank} - {rankCost.description} (年額:
                          {formatCurrency(rankCost.annual_cost)})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="effective_from"
                control={control}
                rules={{ required: '発効日は必須です' }}
                render={({ field }) => (
                  <Box>
                    <RequiredFieldLabel label="発効日" />
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      error={!!errors.effective_from}
                      helperText={errors.effective_from?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={addPositionMutation.isPending}
            >
              {addPositionMutation.isPending ? '更新中...' : '更新'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>所属・職位履歴</DialogTitle>
        <DialogContent>
          {selectedEmployeeHistory && selectedEmployeeHistory.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>発効日</TableCell>
                    <TableCell>終了日</TableCell>
                    <TableCell>部署</TableCell>
                    <TableCell>職級</TableCell>
                    <TableCell>役職</TableCell>
                    <TableCell>ランク</TableCell>
                    <TableCell align="right">年額コスト</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEmployeeHistory.map((history) => (
                    <TableRow key={history.id}>
                      <TableCell>
                        {formatDateJST(history.effective_from)}
                      </TableCell>
                      <TableCell>
                        {history.effective_to
                          ? formatDateJST(history.effective_to)
                          : '現在'}
                      </TableCell>
                      <TableCell>{history.department_name}</TableCell>
                      <TableCell>
                        <Chip label={history.job_grade} size="small" />
                      </TableCell>
                      <TableCell>{history.job_title}</TableCell>
                      <TableCell>
                        <Chip label={history.rank} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(history.annual_cost)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>履歴がありません</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* CSV一括登録ダイアログ */}
      <Dialog open={bulkDialogOpen} onClose={() => { setBulkDialogOpen(false); setImportErrors([]); setCsvFile(null); }} maxWidth="md" fullWidth>
        <DialogTitle>所属・職位一括登録（CSVインポート）</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              CSVファイルから所属・職位情報を一括登録できます。データエクスポートでダウンロードしたCSVを編集してアップロードしてください。
            </Alert>

            {/* ステップ1: データエクスポート */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                ステップ1: 現在のデータをエクスポート
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                現在の所属・職位データをCSVでダウンロードします。このファイルを編集してアップロードできます。
              </Typography>
              <Button variant="outlined" onClick={handleExportData} startIcon={<DownloadIcon />}>
                データエクスポート
              </Button>
            </Paper>

            {/* ステップ2: CSVアップロード */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                ステップ2: 記入済みCSVをアップロード
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                社員ID、部署ID、職級、役職、ランク、発効日を記入したCSVファイルをアップロードします。
              </Typography>

              <Box sx={{ mb: 2 }}>
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="csv-file-upload-positions"
                  type="file"
                  onChange={handleCsvFileChange}
                />
                <label htmlFor="csv-file-upload-positions">
                  <Button variant="outlined" component="span">
                    CSVファイルを選択
                  </Button>
                </label>
                {csvFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    選択されたファイル: {csvFile.name}
                  </Typography>
                )}
              </Box>

              {csvFile && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  注意: 既存の職位情報がある場合、履歴として保存され新しい職位情報に置き換わります。
                </Alert>
              )}
            </Paper>

            {/* インポートエラー表示 */}
            {importErrors.length > 0 && (
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  インポートエラー ({importErrors.length}件)
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>行番号</TableCell>
                        <TableCell>社員ID</TableCell>
                        <TableCell>エラー内容</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importErrors.map((err, index) => (
                        <TableRow key={index}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell>{err.employee_id}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{err.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkDialogOpen(false); setImportErrors([]); setCsvFile(null); }} disabled={csvUploading}>
            キャンセル
          </Button>
          <Button
            onClick={handleCsvUpload}
            variant="contained"
            color="primary"
            disabled={!csvFile || csvUploading}
            startIcon={csvUploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {csvUploading ? 'アップロード中...' : 'アップロードして登録'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PositionManagementPage
